import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, TrendingUp, Loader2, ChevronRight } from "lucide-react";
import { API } from "@/App";
import axios from "axios";

const YEARS = ["2024", "2023", "2022", "2021", "2020", "2019", "2018", "2017"];

const CATEGORIES = [
  "Large Cap", "Mid Cap", "Small Cap", "Multi Cap", "ELSS",
  "Hybrid", "Debt", "Index Fund"
];

export const AnnualReturnsPage = () => {
  const [year, setYear] = useState("2024");
  const [category, setCategory] = useState("Large Cap");
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchFunds = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/funds/search?q=${category}&limit=20`);
        const filtered = (res.data.funds || [])
          .filter(f => f.planType === "Direct" && f.option === "Growth");
        setFunds(filtered);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchFunds();
  }, [category]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
              Mutual Fund <span className="gold-text">Annual Returns</span>
            </h1>
            <p className="text-muted-foreground">
              Year-wise performance of mutual funds
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <Card className="bg-card border-border mb-8">
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">Category</label>
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">Year</label>
                    <Select value={year} onValueChange={setYear}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select year" />
                      </SelectTrigger>
                      <SelectContent>
                        {YEARS.map((y) => (
                          <SelectItem key={y} value={y}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    {category} Funds - {year} Performance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {funds.map((fund, idx) => (
                      <Link key={fund.schemeCode} to={`/research/${fund.schemeCode}`}>
                        <div className="flex items-center justify-between p-3 rounded-lg hover:bg-secondary/50 transition-colors">
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                              {idx + 1}
                            </span>
                            <div>
                              <p className="font-medium text-sm line-clamp-1">{fund.schemeName}</p>
                              <p className="text-xs text-muted-foreground">{fund.category}</p>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
